import React, { useEffect } from 'react';
import styled from 'styled-components';

const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Debug pagination component state
  useEffect(() => {
    console.log('Pagination component:', { currentPage, totalItems, itemsPerPage, totalPages });
  }, [currentPage, totalItems, itemsPerPage, totalPages]);
  
  // If only one page, don't show pagination
  if (totalPages <= 1) {
    console.log('Pagination not showing: totalPages ≤ 1');
    return null;
  }
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    // Always include first page
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // First page
    if (startPage > 1) {
      pages.push(1);
      
      if (startPage > 2) {
        pages.push('...');
      }
    }
    
    // Middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Get the page numbers to display
  const pageNumbers = getPageNumbers();
  console.log('Page numbers:', pageNumbers);

  return (
    <PaginationContainer data-testid="pagination">
      <PaginationButton 
        onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <i className="fas fa-chevron-left"></i>
      </PaginationButton>
      
      {pageNumbers.map((page, index) => (
        page === '...' ? (
          <PaginationEllipsis key={`ellipsis-${index}`}>...</PaginationEllipsis>
        ) : (
          <PaginationButton
            key={`page-${page}`}
            onClick={() => onPageChange(page)}
            active={page === currentPage}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </PaginationButton>
        )
      ))}
      
      <PaginationButton 
        onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        <i className="fas fa-chevron-right"></i>
      </PaginationButton>
    </PaginationContainer>
  );
};

// Styled Components
const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 30px;
  justify-content: center;
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  border: 1px solid var(--border-color);
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  background: ${props => props.active ? 'var(--primary-color)' : 'var(--glass-bg)'};
  border-radius: 8px;
  color: ${props => props.active ? 'white' : 'inherit'};
  border-color: ${props => props.active ? 'var(--primary-color)' : 'var(--border-color)'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.disabled ? 0.5 : 1};
  font-size: 14px;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  }
`;

const PaginationEllipsis = styled.span`
  padding: 0 5px;
`;

export default Pagination; 